import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getTrips, createTrip, deleteTrip } from '../store/tripSlice';
import TripCard from '../components/TripCard';
import TripForm from '../components/TripForm';
import MessageModal from '../components/MessageModal';
import tripService from '../services/tripService';
import { Plus, Search } from 'lucide-react';

const Dashboard = () => {
  const dispatch = useDispatch();
  const { trips, loading } = useSelector(state => state.trips);
  const { user } = useSelector(state => state.auth);

  const [showTripForm, setShowTripForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [favorites, setFavorites] = useState(new Set());
  const [message, setMessage] = useState({ text: '', type: '', show: false });

  useEffect(() => {
    dispatch(getTrips({ search: searchTerm }));
  }, [dispatch, searchTerm]);

  useEffect(() => {
    if (user?.favoriteTrips) {
      setFavorites(new Set(user.favoriteTrips));
    }
  }, [user]);

  const showMessage = (text, type) => {
    setMessage({ text, type, show: true });
  };

  const hideMessage = () => {
    setMessage({ text: '', type: '', show: false });
  };

  const handleCreateTrip = async (tripData) => {
    try {
      const tagsArray = tripData.tags ? tripData.tags.split(',').map(tag => tag.trim()) : [];
      await dispatch(createTrip({ ...tripData, tags: tagsArray })).unwrap();
      setShowTripForm(false);
      showMessage('🎉 Trip created successfully! Ready to plan your adventure!', 'success');
    } catch (error) {
      showMessage(error || 'Failed to create trip. Please try again.', 'error');
    }
  };



  const handleToggleFavorite = async (tripId) => {
    try {
      const result = await tripService.toggleFavorite(tripId);
      if (result.isFavorite) {
        setFavorites(prev => new Set([...prev, tripId]));
        showMessage('❤️ Added to favorites! You can find it in your favorites page.', 'success');
      } else {
        setFavorites(prev => {
          const newFavorites = new Set(prev);
          newFavorites.delete(tripId);
          return newFavorites;
        });
        showMessage('💔 Removed from favorites! Trip removed from your favorites list.', 'info');
      }
    } catch (error) {
      showMessage('Failed to update favorites. Please try again.', 'error');
    }
  };

  const handleDeleteTrip = async (tripId) => {
    if (window.confirm('🗑️ Are you sure you want to delete this trip? This action cannot be undone!')) {
      try {
        await dispatch(deleteTrip(tripId)).unwrap();
        showMessage('🗑️ Trip deleted successfully! Your trip has been permanently removed.', 'success');
      } catch (error) {
        showMessage('Failed to delete trip. Please try again.', 'error');
      }
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    dispatch(getTrips({ search: searchTerm }));
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Trips</h1>
          <p className="text-gray-600 mt-1">Plan and manage your travel adventures</p>
        </div>
        <button
          onClick={() => setShowTripForm(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus className="h-5 w-5" />
          <span>New Trip</span>
        </button>
      </div>

      {/* Search Bar */}
      <form onSubmit={handleSearch} className="mb-8">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search trips..."
            className="input-field pl-10"
          />
        </div>
      </form>

      {/* Trips Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : trips.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <Plus className="h-16 w-16 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No trips yet</h3>
          <p className="text-gray-600 mb-4">Start planning your first adventure!</p>
          <button
            onClick={() => setShowTripForm(true)}
            className="btn-primary"
          >
            Create Your First Trip
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {trips.map((trip, index) => (
            <div key={trip._id} className="relative">
              <TripCard
                trip={trip}
                onToggleFavorite={handleToggleFavorite}
                isFavorite={favorites.has(trip._id)}
              />
              {/* Delete Button */}
              {trip.owner._id === user._id && (
                <button
                  onClick={() => handleDeleteTrip(trip._id)}
                  className="absolute top-4 right-4 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg transition-all duration-200 hover:scale-110"
                  title="Delete Trip"
                >
                  🗑️
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Trip Form Modal */}
      {showTripForm && (
        <TripForm
          onSubmit={handleCreateTrip}
          onClose={() => setShowTripForm(false)}
          isLoading={loading}
        />
      )}

      {/* Message Modal */}
      <MessageModal
        message={message.text}
        type={message.type}
        onClose={hideMessage}
      />
    </div>
  );
};

export default Dashboard;